import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Users, Buildings, Briefcase, ChartBar, CurrencyDollar, Clock,
  Warning as WarningIcon, Trophy, ArrowDown, ArrowUp, DownloadSimple
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';

const formatCLP = (amount) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const COLORS = ['#18181b','#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16','#f97316'];
const STATUS_LABELS = { activo:'Activos', desvinculado:'Desvinculados', vacaciones:'Vacaciones', 'con licencia':'Con Licencia', suspendido:'Suspendidos' };

const StaffingPage = () => {
  const { axiosInstance } = useAuth();
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCargo, setSelectedCargo] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [activeTab, setActiveTab] = useState('dotacion');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { year, month };
      if (selectedBranch) params.branch_id = selectedBranch;
      if (selectedCargo) params.cargo = selectedCargo;

      const [analyticsRes, branchesRes, cargosRes] = await Promise.all([
        axiosInstance.get('/api/analytics/staffing', { params }),
        axiosInstance.get('/api/branches'),
        axiosInstance.get('/api/cargos'),
      ]);
      setData(analyticsRes.data);
      setBranches(branchesRes.data);
      setCargos(cargosRes.data.filter(c => c.is_active));
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, year, month, selectedBranch, selectedCargo]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ year, month, format: 'xlsx' });
      if (selectedBranch) params.append('branch_id', selectedBranch);
      const response = await axiosInstance.get(`/api/export/staffing-report?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dotacion_${year}_${String(month).padStart(2,'0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte exportado');
    } catch { toast.error('Error al exportar'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  const g = data?.global || {};
  const byBranch = data?.staffing_by_branch || {};
  const cargoStats = data?.cargo_stats || {};
  const rankings = data?.rankings || {};

  const branchChartData = Object.entries(byBranch).map(([, v]) => ({
    name: v.branch_name?.replace('Sucursal ', ''),
    total: v.total,
    activos: v.by_status?.activo || 0
  }));

  const cargoChartData = Object.entries(cargoStats).slice(0, 8).map(([name, v]) => ({
    name, workers: v.total_workers
  }));

  const statusPieData = Object.entries(g.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k, value: v
  }));

  const tabs = [
    { id: 'dotacion', label: 'Dotación' },
    { id: 'cargos', label: 'Por Cargo' },
    { id: 'rankings', label: 'Rankings' },
  ];

  return (
    <div className="space-y-5" data-testid="staffing-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="staffing-title">
            Dotación y Analítica
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Análisis de personal por sucursal y cargo</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} className="form-select w-44" data-testid="staffing-branch-filter">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={selectedCargo} onChange={e => setSelectedCargo(e.target.value)} className="form-select w-40" data-testid="staffing-cargo-filter">
            <option value="">Todos los cargos</option>
            {cargos.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} className="form-select w-36" data-testid="staffing-month-filter">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} className="form-select w-24" data-testid="staffing-year-filter">
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={handleExport} className="btn-secondary flex items-center gap-2" data-testid="export-staffing-btn">
            <DownloadSimple size={18} /> Exportar
          </button>
        </div>
      </div>

      {/* Global KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={Users} label="Total Trabajadores" value={g.total_workers} />
        <KpiCard icon={Users} label="Activos" value={g.active_workers} accent="emerald" />
        <KpiCard icon={Users} label="Desvinculados" value={g.desvinculados} accent="red" />
        <KpiCard icon={Users} label="Vacaciones" value={g.by_status?.vacaciones || 0} accent="amber" />
        <KpiCard icon={Users} label="Licencia / Susp." value={(g.by_status?.['con licencia'] || 0) + (g.by_status?.suspendido || 0)} accent="purple" />
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 flex gap-0">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === t.id ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-700'
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'dotacion' && (
        <div className="space-y-6">
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {branchChartData.length > 0 && (
              <div className="chart-container">
                <h3 className="font-heading font-semibold text-zinc-900 mb-4">Trabajadores por Sucursal</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={branchChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                      <Tooltip contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0 }} />
                      <Bar dataKey="total" fill="#18181b" name="Total" />
                      <Bar dataKey="activos" fill="#10b981" name="Activos" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {statusPieData.length > 0 && (
              <div className="chart-container">
                <h3 className="font-heading font-semibold text-zinc-900 mb-4">Distribución por Estado</h3>
                <div className="h-52 flex items-center">
                  <ResponsiveContainer width="50%" height="100%">
                    <PieChart>
                      <Pie data={statusPieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={false}>
                        {statusPieData.map((entry, i) => <Cell key={`cell-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5">
                    {statusPieData.map((s, i) => (
                      <div key={s.name} className="flex items-center gap-2 text-sm">
                        <span className="w-3 h-3 shrink-0" style={{ background: COLORS[i % COLORS.length] }}></span>
                        <span className="text-zinc-600">{s.name}: <strong>{s.value}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Branch Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Object.entries(byBranch).map(([brId, br]) => (
              <div key={brId} className="border border-zinc-200 bg-white p-5" data-testid={`branch-card-${brId}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Buildings size={18} className="text-zinc-600" />
                  <h4 className="font-heading font-semibold text-zinc-900">{br.branch_name}</h4>
                  <span className="ml-auto text-lg font-bold text-zinc-900">{br.total}</span>
                </div>
                {/* Status breakdown */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(br.by_status || {}).filter(([,v]) => v > 0).map(([st, count]) => (
                    <span key={st} className={`text-xs px-2 py-0.5 ${
                      st === 'activo' ? 'bg-emerald-100 text-emerald-800' :
                      st === 'desvinculado' ? 'bg-red-100 text-red-800' :
                      st === 'vacaciones' ? 'bg-amber-100 text-amber-800' :
                      'bg-zinc-100 text-zinc-600'
                    }`}>
                      {st}: {count}
                    </span>
                  ))}
                </div>
                {/* Cargo breakdown */}
                <div className="border-t border-zinc-100 pt-3 space-y-1">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Por Cargo</p>
                  {Object.entries(br.by_cargo || {}).map(([c, count]) => (
                    <div key={c} className="flex justify-between text-sm">
                      <span className="text-zinc-600">{c}</span>
                      <span className="font-medium text-zinc-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'cargos' && (
        <div className="space-y-6">
          {/* Cargo distribution chart */}
          {cargoChartData.length > 0 && (
            <div className="chart-container">
              <h3 className="font-heading font-semibold text-zinc-900 mb-4">Distribución por Cargo</h3>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cargoChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#a1a1aa" />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} stroke="#a1a1aa" />
                    <Tooltip contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0 }} />
                    <Bar dataKey="workers" fill="#18181b" name="Trabajadores" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Cargo detail cards */}
          <div className="space-y-4">
            {Object.entries(cargoStats).map(([cargoName, cs]) => (
              <div key={cargoName} className="border border-zinc-200 bg-white" data-testid={`cargo-stats-${cargoName}`}>
                <div className="flex items-center justify-between p-4 border-b border-zinc-100">
                  <div className="flex items-center gap-2">
                    <Briefcase size={18} className="text-zinc-600" />
                    <h4 className="font-heading font-semibold text-zinc-900">{cargoName}</h4>
                  </div>
                  <span className="text-sm text-zinc-500">{cs.total_workers} trabajadores ({cs.active_workers} activos)</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-0 divide-x divide-zinc-100">
                  <StatCell label="Anticipos" value={formatCLP(cs.advances_total)} sub={`Prom: ${formatCLP(cs.avg_advances)}`} />
                  <StatCell label="Días Trabajados" value={cs.days_worked} sub={`Prom: ${cs.avg_days_worked}`} />
                  <StatCell label="Inasistencias" value={cs.absences} sub={`Prom: ${cs.avg_absences}`} accent={cs.absences > 0 ? 'red' : ''} />
                  <StatCell label="Descansos" value={cs.rest_days} />
                  <StatCell label="Vacaciones" value={cs.vacations} />
                  <StatCell label="Horas Extras" value={cs.overtime_hours} sub={`Prom: ${cs.avg_overtime}`} />
                  <StatCell label="Anticipos/Trab." value={formatCLP(cs.avg_advances)} />
                </div>
                {/* Top 3 workers in this cargo */}
                {cs.workers_detail?.length > 0 && (
                  <div className="p-3 bg-zinc-50 border-t border-zinc-100">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Top Trabajadores (Anticipos)</p>
                    <div className="flex flex-wrap gap-3">
                      {cs.workers_detail.slice(0, 3).map((w, i) => (
                        <span key={w.id} className="text-xs text-zinc-600">
                          <strong className="text-zinc-900">{i + 1}.</strong> {w.name} - {formatCLP(w.advances_total)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'rankings' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cargo Rankings */}
            <RankingTable
              title="Cargos con Mayor Monto de Anticipos"
              icon={CurrencyDollar}
              data={rankings.cargo_advances || []}
              columns={[
                { key: 'cargo', label: 'Cargo' },
                { key: 'total', label: 'Total', format: formatCLP },
                { key: 'avg', label: 'Promedio', format: formatCLP },
                { key: 'workers', label: 'Trabajadores' },
              ]}
            />
            <RankingTable
              title="Cargos con Más Inasistencias"
              icon={WarningIcon}
              data={rankings.cargo_absences || []}
              columns={[
                { key: 'cargo', label: 'Cargo' },
                { key: 'total', label: 'Total' },
                { key: 'avg', label: 'Promedio' },
                { key: 'workers', label: 'Trabajadores' },
              ]}
            />
            <RankingTable
              title="Cargos con Más Horas Extras"
              icon={Clock}
              data={rankings.cargo_overtime || []}
              columns={[
                { key: 'cargo', label: 'Cargo' },
                { key: 'total', label: 'Total Hrs' },
                { key: 'avg', label: 'Prom/Trab.' },
                { key: 'workers', label: 'Trabajadores' },
              ]}
            />
          </div>

          {/* Worker Rankings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <WorkerRankingTable title="Top Trabajadores - Anticipos" icon={CurrencyDollar} data={rankings.top_advances || []} valueKey="advances_total" format={formatCLP} />
            <WorkerRankingTable title="Top Trabajadores - Inasistencias" icon={WarningIcon} data={rankings.top_absences || []} valueKey="absences" />
            <WorkerRankingTable title="Top Trabajadores - Días Trabajados" icon={ChartBar} data={rankings.top_worked || []} valueKey="days_worked" />
            <WorkerRankingTable title="Top Trabajadores - Horas Extras" icon={Clock} data={rankings.top_overtime || []} valueKey="overtime_hours" suffix=" hrs" />
          </div>
        </div>
      )}
    </div>
  );
};

/* --- Subcomponents --- */

const KpiCard = ({ icon: Icon, label, value, accent }) => {
  const accentColor = accent === 'emerald' ? 'text-emerald-600' : accent === 'red' ? 'text-red-600' : accent === 'amber' ? 'text-amber-600' : accent === 'purple' ? 'text-purple-600' : 'text-zinc-600';
  return (
    <div className="kpi-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className={accentColor} />
        <span className="kpi-label">{label}</span>
      </div>
      <div className="kpi-value text-2xl">{value}</div>
    </div>
  );
};

const StatCell = ({ label, value, sub, accent }) => (
  <div className="p-3 text-center">
    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</p>
    <p className={`text-lg font-bold mt-0.5 ${accent === 'red' ? 'text-red-600' : 'text-zinc-900'}`}>{value}</p>
    {sub && <p className="text-[10px] text-zinc-400">{sub}</p>}
  </div>
);

const RankingTable = ({ title, icon: Icon, data, columns }) => (
  <div className="table-container">
    <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
      <Icon size={18} className="text-zinc-600" />
      <h3 className="font-heading font-semibold text-zinc-900 text-sm">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500">#</th>
            {columns.map(col => (
              <th key={col.key} className={`${col.key === columns[0].key ? 'text-left' : 'text-right'} px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500`}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 8).map((row, idx) => (
            <tr key={row.name || row[columns[0]?.key]} className="border-b border-zinc-100">
              <td className="px-3 py-2 text-sm font-medium text-zinc-400">{idx + 1}</td>
              {columns.map(col => (
                <td key={col.key} className={`${col.key === columns[0].key ? 'text-left font-medium text-zinc-900' : 'text-right text-zinc-600'} px-3 py-2 text-sm`}>
                  {col.format ? col.format(row[col.key]) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const WorkerRankingTable = ({ title, icon: Icon, data, valueKey, format, suffix = '' }) => (
  <div className="table-container">
    <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
      <Icon size={18} className="text-zinc-600" />
      <h3 className="font-heading font-semibold text-zinc-900 text-sm">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500">#</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500">Nombre</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500">Sucursal</th>
            <th className="text-right px-3 py-2 text-xs uppercase tracking-wider font-medium text-zinc-500">Valor</th>
          </tr>
        </thead>
        <tbody>
          {data.filter(w => w[valueKey] > 0).slice(0, 10).map((w, i) => (
            <tr key={w.id || i} className="border-b border-zinc-100">
              <td className="px-3 py-2 text-sm font-medium text-zinc-400">{i + 1}</td>
              <td className="px-3 py-2 text-sm font-medium text-zinc-900">{w.name}</td>
              <td className="px-3 py-2 text-sm text-zinc-600">{w.cargo}</td>
              <td className="px-3 py-2 text-sm text-zinc-600">{w.branch}</td>
              <td className="px-3 py-2 text-sm text-right font-mono font-medium text-zinc-900">
                {format ? format(w[valueKey]) : w[valueKey]}{suffix}
              </td>
            </tr>
          ))}
          {data.filter(w => w[valueKey] > 0).length === 0 && (
            <tr><td colSpan={5} className="px-3 py-4 text-sm text-zinc-400 text-center">Sin datos para este período</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default StaffingPage;
